/**
 * Sign-up screen with two phases: register (email + password), then confirm the
 * emailed code with a modern one-time-code input. On confirmation the user is signed
 * in automatically and taken straight to their dashboard (mirroring the wizard gate),
 * so a fresh signup never has to re-enter the credentials they just chose. Cognito
 * errors are mapped to on-brand copy, and an unconfirmed user can resend inline.
 */
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button, Field, Notification } from '@nuff/ui/react';

import { useAuth } from '../auth/context';
import { CodeInput } from '../auth/CodeInput';
import { friendlyAuthMessage, PASSWORD_HINT } from '../auth/errors';

type Phase = 'register' | 'confirm';

export function SignUp(): React.JSX.Element {
  const { status, signUp, confirm, signIn, resendCode } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'signedIn') {
    return <Navigate to="/app" replace />;
  }

  async function handleRegister(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await signUp(email, password);
      setPhase('confirm');
    } catch (caught) {
      setError(friendlyAuthMessage(caught, 'Could not create the account.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(fullCode: string): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await confirm(email, fullCode);
      // Auto-sign-in and go straight to the dashboard, mirroring the wizard gate, so a
      // fresh signup is not bounced to /signin to retype the credentials just chosen.
      await signIn(email, password);
      await navigate('/app');
    } catch (caught) {
      setError(friendlyAuthMessage(caught, 'Could not confirm the code.'));
      setCode('');
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await resendCode(email);
      setNotice(`Code sent. Check ${email}.`);
    } catch (caught) {
      setError(friendlyAuthMessage(caught, 'Could not resend the code.'));
    }
  }

  if (phase === 'confirm') {
    return (
      <section className="auth" aria-labelledby="signup-title">
        <h1 id="signup-title" className="auth__title">
          Check your email
        </h1>
        <p className="auth__lead">
          We sent a 6-digit code to {email}. Enter it below to finish creating your account.
        </p>
        {notice !== null && (
          <p className="auth__status" role="status">
            {notice}
          </p>
        )}
        {error !== null && <Notification variant="error">{error}</Notification>}
        <label className="auth__label" htmlFor="signup-code">
          Verification code
        </label>
        <CodeInput
          label="Verification code"
          value={code}
          onChange={setCode}
          onComplete={(fullCode) => void handleConfirm(fullCode)}
          disabled={submitting}
          autoFocus
        />
        <p className="auth__alt">
          Didn&rsquo;t get it?{' '}
          <button
            type="button"
            className="auth__linkbtn"
            onClick={() => void handleResend()}
            disabled={submitting}
          >
            Resend code
          </button>
        </p>
      </section>
    );
  }

  return (
    <section className="auth" aria-labelledby="signup-title">
      <h1 id="signup-title" className="auth__title">
        Create your account
      </h1>
      <p className="auth__lead">Start setting up newsletters our AI runs for you.</p>
      <form className="auth__form" onSubmit={(event) => void handleRegister(event)} noValidate>
        {error !== null && <Notification variant="error">{error}</Notification>}
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={setEmail}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          hint={PASSWORD_HINT}
          value={password}
          onChange={setPassword}
        />
        <Button className="app__btn-start" type="submit" disabled={submitting}>
          {submitting ? 'Creating...' : 'Create account'}
        </Button>
      </form>
      <p className="auth__alt">
        Already have an account? <Link to="/signin">Sign in</Link>
      </p>
    </section>
  );
}
